import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, TrendingUp, AlertTriangle, Package } from 'lucide-react';
import MetricsCards from '../components/dashboard/MetricsCards';
import ProgramTimeline from '../components/dashboard/ProgramTimeline';
import UpcomingMilestones from '../components/dashboard/UpcomingMilestones';
import AlertsInsights from '../components/dashboard/AlertsInsights';
import QuickIntelligence from '../components/dashboard/QuickIntelligence';
import apiClient from '../api/client';

const WelcomeDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [timelineData, setTimelineData] = useState([]);
  const [milestonesData, setMilestonesData] = useState([]);
  const [alertsData, setAlertsData] = useState(null);
  const [error, setError] = useState(null);

  // Filters
  const [selectedOem, setSelectedOem] = useState('all');
  const [selectedYearRange, setSelectedYearRange] = useState('2024-2028');

  useEffect(() => {
    loadDashboardData();
  }, [selectedOem, selectedYearRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Parse year range
      const [startYear, endYear] = selectedYearRange.split('-');
      const startDate = `${startYear}-01-01`;
      const endDate = `${endYear}-12-31`;

      // Load all dashboard data in parallel
      const [summaryRes, timelineRes, milestonesRes, alertsRes] = await Promise.all([
        apiClient.get('/api/v1/dashboard/summary'),
        apiClient.get('/api/v1/dashboard/timeline', { 
          params: { 
            start_date: startDate, 
            end_date: endDate,
            oem: selectedOem 
          } 
        }),
        apiClient.get('/api/v1/dashboard/milestones', { params: { days: 90 } }),
        apiClient.get('/api/v1/dashboard/alerts')
      ]);

      if (summaryRes.data.success) {
        setSummaryData(summaryRes.data.data);
      }

      if (timelineRes.data.success) {
        setTimelineData(timelineRes.data.data.programs || []);
      }

      if (milestonesRes.data.success) {
        setMilestonesData(milestonesRes.data.data.milestones || []);
      }

      if (alertsRes.data.success) {
        setAlertsData(alertsRes.data.data);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err.message || 'Failed to load dashboard data');
      setLoading(false);
    }
  };

  const handleProgramClick = (oppId) => {
    navigate(`/parts/${oppId}`);
  };

  if (loading && !summaryData) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error && !summaryData) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="text-red-800 font-medium">Error loading dashboard</span>
          </div>
          <p className="text-red-600 mt-2">{error}</p>
          <button
            onClick={loadDashboardData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const userName = summaryData?.user?.name || 'User';
  const currentDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {userName}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{currentDate}</p>
          </div>
          {summaryData?.user?.last_login && (
            <div className="text-sm text-gray-500">
              Last login: {new Date(summaryData.user.last_login).toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 space-y-6">
        {/* Metrics Cards */}
        <MetricsCards data={summaryData?.metrics} loading={loading} />

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">OEM:</label>
              <select
                value={selectedOem}
                onChange={(e) => setSelectedOem(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All OEMs</option>
                <option value="Ford">Ford</option>
                <option value="GM">GM</option>
                <option value="Stellantis">Stellantis</option>
                <option value="Honda">Honda</option>
                <option value="Toyota">Toyota</option>
                <option value="Tesla">Tesla</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Year Range:</label>
              <select
                value={selectedYearRange}
                onChange={(e) => setSelectedYearRange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="2024-2026">2024-2026</option>
                <option value="2024-2027">2024-2027</option>
                <option value="2024-2028">2024-2028</option>
                <option value="2025-2029">2025-2029</option>
              </select>
            </div>
          </div>
        </div>

        {/* Program Timeline */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>Program Launch Timeline</span>
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Gantt view of program SOPs with tariff risk indicators
            </p>
          </div>
          <div className="p-6">
            <ProgramTimeline 
              data={timelineData} 
              onProgramClick={handleProgramClick}
              loading={loading}
            />
          </div>
        </div>

        {/* Side Panels: Milestones and Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Milestones */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <Package className="w-5 h-5" />
                <span>Upcoming Milestones</span>
              </h2>
              <p className="text-sm text-gray-500 mt-1">Next 90 days</p>
            </div>
            <div className="p-6">
              <UpcomingMilestones 
                data={milestonesData} 
                onItemClick={handleProgramClick}
                loading={loading}
              />
            </div>
          </div>

          {/* Alerts & Insights */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5" />
                <span>Alerts & Insights</span>
              </h2>
              <p className="text-sm text-gray-500 mt-1">Portfolio health & changes</p>
            </div>
            <div className="p-6">
              <AlertsInsights 
                data={alertsData}
                loading={loading}
              />
            </div>
          </div>
        </div>

        {/* Quick Intelligence */}
        <QuickIntelligence loading={loading} />
      </div>
    </div>
  );
};

export default WelcomeDashboard;
